import List from '@pelckmans/elementis/components/list';
import { ComponentProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { ExerciseCategory } from '../../../../api/exercise-category';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import useAnalytics from '../../../../hooks/useAnalytics';
import { useRoutes } from '../../../../hooks/useRoutes';
import { ExerciseCategoriesContext } from '../../_contexts/ExerciseCategoriesContext';
import ModuleContext from '../../_contexts/ModuleContext';
import SidebarVisibilityContext from '../../_contexts/SidebarVisibilityContext';
import { useCategoryLinkBuilder } from '../hooks/useCategoryLinkBuilder';

interface MappedExerciseCategory extends ExerciseCategory {
  level: number;
}

function VirtualExerciseModuleTOC() {
  const { t } = useTranslation();
  const { buildAbsolutePath } = useRoutes();
  const { subjectSlug, methodSlug, selectedCategoryId } = useParams();
  const { module } = useContext(ModuleContext);
  const location = useLocation();
  const { buildCategoryLink } = useCategoryLinkBuilder();
  const analytics = useAnalytics();

  const { exerciseCategories, loading } = useContext(ExerciseCategoriesContext);
  const { isSidebarVisible, setSidebarVisibility } = useContext(SidebarVisibilityContext);

  const getAppearance = (exerciseCategory: MappedExerciseCategory) => {
    if (exerciseCategory.id === selectedCategoryId) return 'active';

    return exerciseCategory.level === 0 ? 'prominent' : 'default';
  };

  if (!subjectSlug || !methodSlug || !module) return null;

  function handleClick(categoryId?: string) {
    if (isSidebarVisible) {
      setSidebarVisibility(false);
    }

    if (categoryId) {
      analytics.capture({
        eventType: ANALYTICS_EVENT_TYPES.NODE_ACCESSED,
        objectId: categoryId,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_VIRTUAL_EXERCISE_MODULE,
      });
    }
  }

  const renderExerciseCategories = (exerciseCategories: ExerciseCategory[], level = 0): (JSX.Element | null)[] => {
    return exerciseCategories.flatMap(exerciseCategory => {
      const mappedExerciseCategory = { ...exerciseCategory, level };

      return [
        <List.Item
          key={exerciseCategory.id}
          title={exerciseCategory.name}
          level={mappedExerciseCategory.level as ComponentProps<typeof List.Item>['level']}
          as="link"
          href={buildCategoryLink(exerciseCategory.id)}
          appearance={getAppearance(mappedExerciseCategory)}
          onClick={() => handleClick(exerciseCategory.id)}
        />,
        ...(exerciseCategory.categories ? renderExerciseCategories(exerciseCategory.categories, level + 1) : []),
      ];
    });
  };

  const overviewHref = buildAbsolutePath(['MATERIAL', 'MATERIAL.CONTENT', 'MATERIAL.VEM'], {
    subjectSlug: subjectSlug,
    methodSlug: methodSlug,
    moduleSlug: module.slug,
  });

  return (
    <List
      showSeparators={false}
      isLoading={loading}
      loadingLabel={t('PAGES.VEM.SIDEBAR.TOC.LOADING.A11Y_LABEL')}
      className="list-items"
    >
      <List.Item
        title={t('PAGES.VEM.SIDEBAR.TOC.OVERVIEW')}
        as="link"
        href={overviewHref}
        appearance={location.pathname === overviewHref ? 'active' : 'prominent'}
        onClick={() => handleClick()}
      />
      {renderExerciseCategories(exerciseCategories)}
    </List>
  );
}

export default VirtualExerciseModuleTOC;
